import React, { ReactNode, useContext, useRef, useState } from 'react';

import * as LDClient from 'launchdarkly-js-client-sdk';

import { SupportedLanguages, SupportedRegions } from '@rbi-ctg/frontend';
import useEffectOnce from 'hooks/use-effect-once';
import useReadyQueue from 'hooks/use-ready-queue';
import { loadRegion } from 'state/intl/hook';
import { getName } from 'utils/attributes';
import { isProduction, sanityDataset } from 'utils/environment';
import LaunchDarklyHelper, {
  BooleanFlags,
  EnumFlagTypes,
  EnumFlags,
  FlagType,
  LaunchDarklyFlag,
  LaunchDarklyFlagsObject,
  NumericFlags,
  StringFlags,
  UserAttributeUpdates
} from 'utils/launchdarkly';
import LogRocketHelper from 'utils/logrocket';
import noop from 'utils/noop';

import { getFeatureFlagOverridesFromQueryParameters } from './feature-flag-overrides';

export { LaunchDarklyFlag };

const LD_LOGIN_TIMEOUT_MS = 2000;

interface ILogin {
  cognitoId?: string;
  details: { name?: string; email: string };
}
export interface ILaunchDarklyCtx {
  attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDClient.LDUser
  ): Promise<FlagType<F> | undefined>;
  attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDClient.LDUser,
    defaultValue: FlagType<F>
  ): Promise<FlagType<F>>;
  flags: LaunchDarklyFlagsObject;
  flagOverrides: { [id: string]: boolean };
  login: (u: ILogin) => void;
  logout: () => void;
  updateUser: (u: UserAttributeUpdates) => void;
  updateUserAttributes: (u: UserAttributeUpdates) => void;
  updateUserLocale: (locale: { region: SupportedRegions; language: SupportedLanguages }) => void;
  updateUserDeviceId: (deviceId: string) => void;
}

export const LDContext = React.createContext<ILaunchDarklyCtx>({
  attemptGetUpdatedLdFlag: () => Promise.resolve(false as any),
  flags: {},
  flagOverrides: {},
  updateUserDeviceId: noop,
  login: noop,
  logout: noop,
  updateUser: noop,
  updateUserAttributes: noop,
  updateUserLocale: noop
});

export const useLDContext = () => useContext(LDContext);

export function useFlag<F extends EnumFlags>(flag: F): EnumFlagTypes<F>;
export function useFlag(flag: NumericFlags): FlagType<NumericFlags>;
export function useFlag(flag: StringFlags): FlagType<StringFlags>;
export function useFlag(flag: BooleanFlags): FlagType<BooleanFlags>;
export function useFlag(flag: LaunchDarklyFlag): FlagType<LaunchDarklyFlag> {
  const { flags, flagOverrides } = useLDContext();

  if (!isProduction && flagOverrides[flag] !== undefined) {
    return flagOverrides[flag];
  }

  // @todo update code to handle missing flags or update function to accept a fallback
  return flags && flags[flag]!;
}

export function LDProvider(props: { children: ReactNode; ldFlags: LaunchDarklyFlagsObject }) {
  const [flags, setFlags] = useState<LaunchDarklyFlagsObject>(props.ldFlags);
  const flagOverrides = useRef(getFeatureFlagOverridesFromQueryParameters());
  const ldClient = useRef<LDClient.LDClient>();
  const { drainQueue, enqueueIfNotDrained } = useReadyQueue();

  useEffectOnce(() => {
    ldClient.current = LaunchDarklyHelper.getInstance().launchDarkly;

    if (!ldClient.current) {
      // Launch Darkly client failed to initialize and we have rendered the app without it so do nothing
      return;
    }
    drainQueue();

    // runs if a change to flag happens while the user runs the app
    // WEB ONLY: runs if a user logs in
    ldClient.current.on('change', changes => {
      const lrInstance = LogRocketHelper.getInstance().logrocket;
      const flattened = LaunchDarklyHelper.flattenChanges(changes);
      const logRocketFlag = flattened[LaunchDarklyFlag.ENABLE_LOGROCKET];

      setFlags(state => ({
        ...state,
        ...flattened
      }));

      // If the logrocket flag is true and we haven't already initialized logrocket
      if (logRocketFlag && !lrInstance) {
        LogRocketHelper.init();
      }
    });

    return () => {
      if (ldClient.current) {
        ldClient.current.close();
      }
    };
  });

  const updateLDUser = async (changes: UserAttributeUpdates | null) => {
    const ldInstance = LaunchDarklyHelper.getInstance();
    const updatedFlags = changes
      ? (await ldInstance.updateCurrentUser(changes))?.newFlags
      : (await ldInstance.clearCurrentUser())?.newFlags;

    // MOBILE ONLY:
    // We have to update the flags manually because ldClient's change event
    // watcher doesn't fire on login.
    if (updatedFlags) {
      setFlags(state => ({
        ...state,
        ...updatedFlags
      }));
    }

    return { updatedFlags };
  };

  const updateUser = enqueueIfNotDrained((changes: UserAttributeUpdates) => updateLDUser(changes));

  const updateUserAttributes = enqueueIfNotDrained(
    (user: { cognitoId: string; name: string; email: string }): void => {
      return updateUser({
        ...getName({ name: user.name }, { firstName: '', lastName: '' }),
        key: user.cognitoId,
        name: user.name,
        email: user.email
      });
    }
  );

  const login = enqueueIfNotDrained(({ cognitoId, details: { name = '', email } }: ILogin) => {
    return updateUser({
      ...getName({ name }, { firstName: '', lastName: '' }),
      key: cognitoId,
      name,
      email,
      anonymous: false,
      custom: {
        sanityDataset: `${sanityDataset}_${loadRegion().toLowerCase()}`
      }
    });
  });

  const logout = enqueueIfNotDrained(() => updateLDUser(null));

  const updateUserDeviceId = enqueueIfNotDrained(deviceId => {
    return updateUser({ custom: { device_id: deviceId } });
  });

  const updateUserLocale = enqueueIfNotDrained(
    (locale: { region: SupportedRegions; language: SupportedLanguages }) => {
      return updateUser({
        custom: {
          language: locale.language,
          sanityDataset: `${sanityDataset}_${loadRegion().toLowerCase()}`
        },
        country: locale.region
      });
    }
  );

  /**
   * Attempts to get the LD user's flag value
   *
   * If the request takes longer than one second, we resolve
   * to the value already set in state. This allows us to wait on
   * LD's identify request.
   *
   */
  async function attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDClient.LDUser,
    defaultFlagValue: FlagType<F>
  ): Promise<FlagType<F>>;
  async function attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDClient.LDUser
  ): Promise<FlagType<F> | undefined>;
  async function attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDClient.LDUser,
    defaultFlagValue?: FlagType<F>
  ): Promise<FlagType<F> | undefined> {
    return new Promise<FlagType<F> | undefined>(res => {
      const resolveWithFlagValue = (flagValue?: LaunchDarklyFlagsObject[F]) =>
        res((flagValue ?? defaultFlagValue) as FlagType<F>);

      if (!ldClient?.current) {
        return resolveWithFlagValue(flags[flagKey]);
      }
      let timedOut = false;
      let finished = false;

      setTimeout(() => {
        if (finished) {
          return;
        }
        timedOut = true;
        return resolveWithFlagValue(flags[flagKey]);
      }, LD_LOGIN_TIMEOUT_MS);

      updateLDUser(attributes).then(({ updatedFlags }) => {
        if (timedOut || !updatedFlags) {
          return;
        }
        finished = true;
        return resolveWithFlagValue(updatedFlags[flagKey]);
      });
    }).catch(() => {
      return (flags[flagKey] ?? defaultFlagValue) as FlagType<F>;
    });
  }

  return (
    <LDContext.Provider
      value={{
        attemptGetUpdatedLdFlag,
        login,
        logout,
        updateUser,
        updateUserAttributes,
        updateUserLocale,
        updateUserDeviceId,
        flags,
        flagOverrides: flagOverrides.current
      }}
    >
      {props.children}
    </LDContext.Provider>
  );
}

export default LDContext.Consumer;
